import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { AdvisorLayerMode } from '../enums/advisorLayerMode'
import { UseAdvisorLayerMode } from '../types/UseAdvisorLayerMode'
import { getModeFromUrl } from '../utils/advisorLayerModeFromUrl'
import { buildSearchWithMode, getModeFromSearch } from '../utils/advisorLayerModeSearchParam'
import { readStoredMode, writeStoredMode } from '../utils/advisorLayerModeStorage'

const AdvisorLayerModeContext = createContext<UseAdvisorLayerMode | undefined>(undefined)

interface AdvisorLayerModeProviderProps {
  children?: ReactNode
}

export const AdvisorLayerModeProvider = ({ children }: AdvisorLayerModeProviderProps): ReactElement => {
  const location = useLocation()
  const navigate = useNavigate()
  const [modeState, setModeState] = useState<AdvisorLayerMode>(() => readStoredMode() ?? AdvisorLayerMode.Client)

  const urlMode = useMemo(() => getModeFromSearch(location.search), [location.search])
  // Keeps the toggle in sync when the user lands on a route for the other layer
  // directly (typed URL, bookmark, back/forward) instead of via the toggle itself.
  // getModeFromUrl is the shared precedence (query param > path segment) also used by
  // useResolvedAdvisorLayerMode, so the two React roots can't resolve to different modes.
  const resolvedMode = useMemo(
    () => getModeFromUrl(location.pathname, location.search) ?? modeState,
    [location.pathname, location.search, modeState],
  )

  const applyMode = useCallback((nextMode: AdvisorLayerMode) => {
    setModeState(nextMode)
    writeStoredMode(nextMode)
  }, [])

  const setMode = useCallback(
    (nextMode: AdvisorLayerMode) => {
      applyMode(nextMode)
      if (urlMode && urlMode !== nextMode) {
        navigate({ search: buildSearchWithMode(location.search, nextMode) }, { replace: true })
      }
    },
    [applyMode, location.search, navigate, urlMode],
  )

  useEffect(() => {
    if (resolvedMode !== modeState) {
      applyMode(resolvedMode)
    }
  }, [resolvedMode, modeState, applyMode])

  const value = useMemo<UseAdvisorLayerMode>(() => ({ mode: resolvedMode, setMode }), [resolvedMode, setMode])

  return <AdvisorLayerModeContext.Provider value={value}>{children}</AdvisorLayerModeContext.Provider>
}

export const useAdvisorLayerModeContext = (): UseAdvisorLayerMode => {
  const context = useContext(AdvisorLayerModeContext)

  if (!context) {
    throw new Error('AdvisorLayerModeContext is missing in the module!')
  }

  return context
}
